import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ShoppingCart, User, Menu, X, LogOut } from 'lucide-react';
import { useApp } from '../context/AppContext';

const Navbar = () => {
  const { user, cart, logout, sectionVisibility } = useApp();
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();

  const handleLogin = () => {
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-[#050505]/80 backdrop-blur-xl border-b border-[#D4AF37]/10 transition-all duration-300">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="flex items-center justify-between h-20">
          <Link to="/" className="flex items-center gap-3 group" data-testid="logo">
            <div className="w-10 h-10 bg-gradient-to-br from-[#D4AF37] to-[#FFD700] rounded-lg flex items-center justify-center transition-all duration-300 group-hover:scale-110 group-hover:shadow-lg group-hover:shadow-[#D4AF37]/30">
              <span className="brand-font font-bold text-xl text-black">23</span>
            </div>
            <span className="brand-font font-bold text-xl tracking-tight hidden sm:block text-[#D4AF37] transition-all duration-300 group-hover:text-[#FFD700]">
              23madethis
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-8">
            {sectionVisibility?.beats && (
              <Link to="/beats" className="relative text-neutral-400 hover:text-[#D4AF37] transition-all duration-300 group" data-testid="nav-beats">
                Beats
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-[#D4AF37] transition-all duration-300 group-hover:w-full"></span>
              </Link>
            )}
            {sectionVisibility?.services && (
              <Link to="/services" className="relative text-neutral-400 hover:text-[#D4AF37] transition-all duration-300 group" data-testid="nav-services">
                Services
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-[#D4AF37] transition-all duration-300 group-hover:w-full"></span>
              </Link>
            )}
            {sectionVisibility?.studio && (
              <Link to="/studio" className="relative text-neutral-400 hover:text-[#D4AF37] transition-all duration-300 group" data-testid="nav-studio">
                Book Studio
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-[#D4AF37] transition-all duration-300 group-hover:w-full"></span>
              </Link>
            )}
            {user && (
              <Link to="/dashboard" className="relative text-neutral-400 hover:text-[#D4AF37] transition-all duration-300 group" data-testid="nav-dashboard">
                Dashboard
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-[#D4AF37] transition-all duration-300 group-hover:w-full"></span>
              </Link>
            )}
          </div>

          <div className="flex items-center gap-4">
            <Link to="/cart" className="relative p-2 text-neutral-400 hover:text-[#D4AF37] transition-all duration-300 hover:scale-110" data-testid="cart-button">
              <ShoppingCart size={22} />
              {cart.length > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-[#D4AF37] rounded-full text-xs flex items-center justify-center text-black font-bold animate-bounce-in">
                  {cart.length}
                </span>
              )}
            </Link>

            {user ? (
              <div className="hidden md:flex items-center gap-3">
                <Link to="/dashboard" className="flex items-center gap-2 text-neutral-300 hover:text-[#D4AF37]">
                  {user.picture ? (
                    <img src={user.picture} alt="" className="w-8 h-8 rounded-full border border-[#D4AF37]/30" />
                  ) : (
                    <User size={20} />
                  )}
                  <span className="text-sm">{user.name?.split(' ')[0]}</span>
                </Link>
                <button onClick={logout} className="p-2 text-neutral-500 hover:text-[#D4AF37]" data-testid="logout-btn">
                  <LogOut size={18} />
                </button>
              </div>
            ) : (
              <button onClick={handleLogin} className="btn-primary hidden md:block" data-testid="login-btn">
                Sign In
              </button>
            )}

            <button onClick={() => setMenuOpen(!menuOpen)} className="md:hidden p-2 text-[#D4AF37]" data-testid="mobile-menu-btn">
              {menuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {menuOpen && (
        <div className="md:hidden bg-[#0A0A0A] border-t border-[#D4AF37]/10 py-4 px-6">
          <div className="flex flex-col gap-4">
            {sectionVisibility?.beats && (
              <Link to="/beats" onClick={() => setMenuOpen(false)} className="text-neutral-300 py-2 hover:text-[#D4AF37]">Beats</Link>
            )}
            {sectionVisibility?.services && (
              <Link to="/services" onClick={() => setMenuOpen(false)} className="text-neutral-300 py-2 hover:text-[#D4AF37]">Services</Link>
            )}
            {sectionVisibility?.studio && (
              <Link to="/studio" onClick={() => setMenuOpen(false)} className="text-neutral-300 py-2 hover:text-[#D4AF37]">Book Studio</Link>
            )}
            {user ? (
              <>
                <Link to="/dashboard" onClick={() => setMenuOpen(false)} className="text-neutral-300 py-2 hover:text-[#D4AF37]">Dashboard</Link>
                <button onClick={() => { logout(); setMenuOpen(false); }} className="text-left text-neutral-300 py-2 hover:text-[#D4AF37]">Logout</button>
              </>
            ) : (
              <button onClick={handleLogin} className="btn-primary w-full mt-2">Sign In</button>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
